/**
 * MCP tools: the typed command surface — state snapshot, command discovery,
 * single-command execution, scripted macro replay and workspace switching.
 *
 * These four tools are what make the bridge "complete": every capability
 * reachable from the command line (CAD drafting, 3D, canonical presets,
 * ribbon registry actions, plugin commands) is reachable by an AI agent
 * through exactly the same dispatcher a human uses.
 */

import { dispatchCommand, getDispatchSuggestions } from '../../core/CommandDispatcher';
import { getAllCommands } from '../../modules/cad/CommandParser';
import { CANONICAL_COMMANDS, type CanonicalCommand } from '../../core/commands/canonicalCommands';
import { useCommandPresetStore, effectiveNames } from '../../state/useCommandPresetStore';
import { useAppStore } from '../../state/store';
import { useBIMStore } from '../../state/useBIMStore';
import { useDocumentStore } from '../../state/useDocumentStore';
import { useViewStore, useBlockStore } from '../../state/commandAuxStores';
import { useLayoutStore } from '../../state/useLayoutStore';
import { useSystemVarsStore, SYSTEM_VAR_DEFAULTS } from '../../state/useSystemVarsStore';
import { usePluginStore } from '../../plugins/PluginSystem';
import { isViewportCaptureAvailable } from './viewportCapture';
import type { McpToolHandler } from './mcpResultUtils';
import { asNumber, asString, asStringArray, fail, okJson, okText } from './mcpResultUtils';
/** Cap id arrays so a screenshot-style flood never enters the agent context. */
const ID_CAP = 500;

export const handleGetState: McpToolHandler = () => {
    const app = useAppStore.getState();
    const bim = useBIMStore.getState();
    const doc = useDocumentStore.getState();
    const ledger = Object.values(doc.sceneObjects);
    const bySource: Record<string, number> = {};
    for (const o of ledger) bySource[o.source] = (bySource[o.source] ?? 0) + 1;

    return okJson({
        mode: app.mode,
        activeTool: app.activeTool,
        orthoMode: app.orthoMode,
        selectionCount: app.selectedIds.length,
        selectedIds: app.selectedIds.slice(0, ID_CAP),
        cadEntities: Object.keys(app.entities).length,
        layers: Object.values(app.layers).map((l) => ({
            id: l.id, name: l.name, visible: l.visible, locked: l.locked,
        })),
        activeLayerId: app.activeLayerId,
        view: {
            viewMode: app.view.viewMode,
            projection: app.view.projection,
            gridVisible: app.view.gridVisible,
            axesVisible: app.view.axesVisible,
        },
        document: {
            displayName: doc.displayName,
            kind: doc.kind,
            isDirty: doc.isDirty,
        },
        bim: {
            projectName: bim.project.name,
            elementCount: bim.project.elements.size,
            activeViewId: bim.project.activeViewId,
            levelCount: bim.project.levels.size,
            // Options Bar state: dimension text placement, spot-elevation
            // mode, wall height… readable so an agent can see what the next
            // tool invocation will honor.
            toolProperties: JSON.parse(JSON.stringify(bim.toolProperties)),
        },
        sceneLedger: { total: ledger.length, bySource },
        // 3D HISTORY toggle: when on, the 29 history-aware derived
        // commands (SrfPt-style builders, Project/Pull, Extract*, Section,
        // Make2D, Contour, loft/bridge/MeshToNurb…) record provenance and
        // their outputs recompute when a source entity changes.
        recordHistory: useViewStore.getState().recordHistory,
        // CAD paper space (Faz 9): which layout is active, how many exist.
        paperSpace: (() => {
            const ls = useLayoutStore.getState();
            const active = ls.layouts.find((l) => l.id === ls.activeId);
            return {
                space: active ? 'paper' : 'model',
                activeLayout: active ? active.name : null,
                layouts: ls.layouts.map((l) => ({ name: l.name, viewports: l.viewports.length })),
            };
        })(),
        // 2D system variables (PICKFIRST/FILLMODE/EDGEMODE/PDMODE/
        // FILLETRADIUS/SAVETIME...) — readable + settable via typed commands.
        sysvars: Object.fromEntries(
            Object.keys(SYSTEM_VAR_DEFAULTS).map((k) => [k, (useSystemVarsStore.getState() as unknown as Record<string, number>)[k]])
        ),
        blockDefs: Object.keys(useBlockStore.getState().defs).length,
        plugins: usePluginStore.getState().getAllPlugins().filter((p) => p.enabled).length,
        viewportCaptureReady: isViewportCaptureAvailable(),
    });
};

export const handleListCommands: McpToolHandler = (args) => {
    const filter = (asString(args.filter) ?? '').toLowerCase();
    const section = (asString(args.section) ?? '').toLowerCase();
    const limit = Math.min(Math.max(asNumber(args.limit) ?? 200, 1), 2000);

    const presetState = useCommandPresetStore.getState();
    const preset = presetState.presets.find((p) => p.id === presetState.activePresetId);

    type Row = {
        names: string[];
        id: string;
        label: string;
        section: string;
        kind: CanonicalCommand['kind'];
        description: string;
    };
    const rows: Row[] = [];
    const seenNames = new Set<string>();

    for (const cmd of CANONICAL_COMMANDS) {
        if (section && cmd.section !== section) continue;
        const names = preset ? effectiveNames(preset, cmd) : cmd.names['2d'].concat(cmd.names['3d']);
        const haystack = `${names.join(' ')} ${cmd.label} ${cmd.id} ${cmd.description}`.toLowerCase();
        if (filter && !haystack.includes(filter)) continue;
        rows.push({
            names,
            id: cmd.id,
            label: cmd.label,
            section: cmd.section,
            kind: cmd.kind,
            description: cmd.description,
        });
        names.forEach((n) => seenNames.add(n.toUpperCase()));
    }

    // Legacy parser commands not reachable by the same typed spelling.
    for (const legacy of getAllCommands()) {
        const id = `legacy.${legacy.name}`;
        if (seenNames.has(legacy.name.toUpperCase())) continue;
        const haystack = `${[legacy.name, ...legacy.aliases].join(' ')} ${legacy.description}`.toLowerCase();
        if (filter && !haystack.includes(filter)) continue;
        if (section) continue; // legacy entries have no canonical section
        rows.push({
            names: [legacy.name, ...legacy.aliases],
            id,
            label: legacy.name,
            section: 'system',
            kind: 'system',
            description: legacy.description,
        });
    }

    const truncated = rows.length > limit;
    return okJson({
        total: rows.length,
        returned: Math.min(rows.length, limit),
        truncated,
        commands: rows.slice(0, limit),
    }, `${rows.length} commands available${filter ? ` for filter '${filter}'` : ''}.`);
};

export const handleExecuteCommand: McpToolHandler = (args) => {
    const command = asString(args.command);
    if (!command) return fail("Missing required argument 'command'.");
    const extra = asStringArray(args.args);
    const line = extra && extra.length ? `${command} ${extra.join(' ')}` : command;
    const result = dispatchCommand(line);
    const text = `${result.success ? 'OK' : 'FAILED'} [${result.source}] ${result.message}`;
    if (!result.success) {
        // Offer near-miss suggestions so the agent can self-correct.
        const suggestions = getDispatchSuggestions(command.trim().toUpperCase().split(/\s+/)[0]);
        const hint = suggestions.length ? ` Did you mean: ${suggestions.slice(0, 6).join(', ')}?` : '';
        return fail(`${text}${hint}`);
    }
    return okText(`${text}.`);
};

export const handleRunScript: McpToolHandler = (args) => {
    const lines = asStringArray(args.lines);
    if (!lines || lines.length === 0) return fail("Missing required argument 'lines' (array of command strings).");
    const continueOnError = args.continueOnError === true;

    const steps: { line: string; success: boolean; message: string; source: string }[] = [];
    let failed = 0;
    for (const line of lines) {
        const result = dispatchCommand(line);
        steps.push({
            line,
            success: result.success,
            message: result.message,
            source: result.source,
        });
        if (!result.success) {
            failed += 1;
            if (!continueOnError) break;
        }
    }
    const done = steps.filter((s) => s.success).length;
    return okJson({ executed: steps.length, succeeded: done, failed, steps },
        `Script finished: ${done}/${steps.length} line(s) succeeded.`);
};

export const handleSetToolOption: McpToolHandler = (args) => {
    const key = asString(args.key);
    if (!key) return fail("Missing required argument 'key' (a toolProperties name).");
    const value = args.value;
    if (value === undefined || value === null) {
        return fail("Missing required argument 'value' (string, number or boolean).");
    }
    if (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean') {
        return fail("'value' must be a string, number or boolean.");
    }
    const bim = useBIMStore.getState();
    const known = Object.prototype.hasOwnProperty.call(bim.toolProperties, key)
        // Accept fresh option keys too (default toolProperties only seed a
        // subset), but guard against obvious typos with the camelCase check.
        || /^[a-z][A-Za-z0-9]*$/.test(key);
    if (!known) return fail(`Invalid option key '${key}' — expected a camelCase property name.`);
    bim.setToolProperty(key as never, value as never);
    return okJson({ key, value }, `Tool option '${key}' set to ${JSON.stringify(value)}.`);
};

export const handleSetMode: McpToolHandler = (args) => {
    const mode = asString(args.mode);
    const valid = ['bim', '2d', '3d', 'sketch'];
    if (!mode || !valid.includes(mode)) {
        return fail(`Invalid or missing 'mode'. Expected one of: ${valid.join(', ')}.`);
    }
    useAppStore.getState().setMode(mode as 'bim' | '2d' | '3d' | 'sketch');
    // Layout listens for this and performs the router navigation.
    window.dispatchEvent(new CustomEvent('yap:set-workspace', { detail: { workspace: mode } }));
    return okText(`Workspace switched to '${mode}'.`);
};
